import { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AudioPlayerHandle {
  play: () => void;
  pause: () => void;
  toggle: () => void;
}

interface AudioPlayerProps {
  src: string;
  className?: string;
  autoPlay?: boolean;
  compact?: boolean; // Compact mode: just progress bar and time, no play button
  isPlayingExternal?: boolean; // External control of play state (for Play All feature)
  onEnded?: () => void;
  onPause?: () => void;
  onPlay?: () => void;
  onDurationLoaded?: (duration: number) => void;
}

/**
 * Format seconds into MM:SS display
 */
function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Custom audio player with clean, professional design.
 * Features: play/pause, progress bar, time display, animated waveform.
 * Supports ref-based control via AudioPlayerHandle for direct play/pause.
 */
export const AudioPlayer = forwardRef<AudioPlayerHandle, AudioPlayerProps>(({
  src,
  className,
  autoPlay = false,
  compact = false,
  isPlayingExternal,
  onEnded,
  onPause,
  onPlay,
  onDurationLoaded,
}, ref) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Track if external play was requested while loading
  const pendingExternalPlayRef = useRef(false);

  // Expose play/pause methods via ref
  useImperativeHandle(ref, () => ({
    play: () => {
      const audio = audioRef.current;
      if (audio) {
        audio.play().catch(console.error);
      }
    },
    pause: () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
      }
    },
    toggle: () => {
      const audio = audioRef.current;
      if (audio) {
        if (isPlaying) {
          audio.pause();
        } else {
          audio.play().catch(console.error);
        }
      }
    },
  }), [isPlaying]);

  // Handle play/pause
  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((err) => {
        console.error('Audio playback failed:', err);
        setError('Playback failed');
      });
    }
  }, [isPlaying]);

  // Handle clicking on progress bar to seek
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const progressBar = progressRef.current;
    if (!audio || !progressBar || !isFinite(duration) || duration === 0) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    audio.currentTime = Math.max(0, Math.min(newTime, duration));
    setCurrentTime(audio.currentTime);
  }, [duration]);

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      if (isFinite(audio.duration)) {
        onDurationLoaded?.(audio.duration);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      onPlay?.();
    };
    const handlePause = () => {
      setIsPlaying(false);
      onPause?.();
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onEnded?.();
    };

    const handleError = () => {
      setError('Failed to load audio');
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      // If external play was requested while loading, play now
      if (pendingExternalPlayRef.current) {
        pendingExternalPlayRef.current = false;
        audio.play().catch(console.error);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [onDurationLoaded, onEnded, onPause, onPlay]);

  // Handle autoPlay prop changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || isLoading) return;

    if (autoPlay && !isPlaying) {
      audio.play().catch(console.error);
    }
  }, [autoPlay, isLoading, isPlaying]);

  // Handle external play/pause control (for Play All feature)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || isPlayingExternal === undefined) return;

    if (isPlayingExternal && !isPlaying) {
      if (isLoading) {
        // Audio not ready yet, set pending flag to play when ready
        pendingExternalPlayRef.current = true;
      } else {
        audio.play().catch(console.error);
      }
    } else if (!isPlayingExternal && isPlaying) {
      audio.pause();
    } else if (!isPlayingExternal) {
      // Clear pending flag if external play is cancelled
      pendingExternalPlayRef.current = false;
    }
  }, [isPlayingExternal, isLoading, isPlaying]);

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (error) {
    return (
      <span className="text-xs text-destructive">{error}</span>
    );
  }

  // Compact mode: waveform, progress bar, and time (no play button)
  if (compact) {
    return (
      <div className={cn('flex items-center gap-2 w-full', className)}>
        {/* Hidden audio element */}
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          crossOrigin="anonymous"
        />

        {/* Animated waveform bars */}
        <div className="flex items-end gap-[2px] h-4 shrink-0">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="w-[2px] rounded-sm bg-primary"
              style={{
                height: isPlaying ? undefined : `${4 + (i % 3) * 2}px`,
                animation: isPlaying
                  ? `audioWaveCompact 0.5s ease-in-out ${i * 0.1}s infinite`
                  : 'none',
              }}
            />
          ))}
        </div>

        {/* Progress bar */}
        <div
          ref={progressRef}
          onClick={handleProgressClick}
          className="flex-1 h-1 bg-muted rounded-full cursor-pointer relative overflow-hidden"
        >
          <div
            className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Time display */}
        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>

        {/* Inline keyframes for compact waveform animation */}
        <style>{`
          @keyframes audioWaveCompact {
            0%, 100% { height: 4px; }
            50% { height: 14px; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-3 w-full', className)}>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        crossOrigin="anonymous"
      />

      {/* Play/Pause button */}
      <button
        onClick={togglePlayPause}
        disabled={isLoading}
        className="flex items-center justify-center h-8 w-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isPlaying ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4 ml-0.5" />
        )}
      </button>

      {/* Waveform / Progress section */}
      <div className="flex-1 flex items-center gap-3 min-w-0">
        {/* Animated waveform bars */}
        <div className="flex items-end gap-[3px] h-6 shrink-0">
          {[0, 1, 2, 3, 4].map((i) => {
            const heights = [8, 12, 16, 12, 8];
            return (
              <span
                key={i}
                className="w-[3px] rounded-sm bg-primary"
                style={{
                  height: isPlaying ? undefined : `${heights[i]}px`,
                  animation: isPlaying
                    ? `audioWave 0.6s ease-in-out ${i * 0.1}s infinite`
                    : 'none',
                }}
              />
            );
          })}
        </div>

        {/* Progress bar */}
        <div
          ref={progressRef}
          onClick={handleProgressClick}
          className="flex-1 h-2 bg-muted rounded-full cursor-pointer relative overflow-hidden min-w-[80px]"
        >
          <div
            className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Time display */}
        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>

      {/* Inline keyframes for waveform animation */}
      <style>{`
        @keyframes audioWave {
          0%, 100% { height: 6px; }
          50% { height: 20px; }
        }
      `}</style>
    </div>
  );
});
